import { Badge, Box, Chip, Tooltip, Typography } from '@mui/material';
import { RiInformationLine, RiQuestionnaireFill } from '@remixicon/react';
import { useTranslation } from 'react-i18next';
import { BuzonNucStatusEnum } from '@/shared/types/interfaces/BuzonJudicial.interface';
import type { BuzonNucPreference } from '@/shared/types/interfaces/BuzonJudicial.interface';

interface BuzonEstadoChipProps {
  row: BuzonNucPreference;
}

/** Estados chip — extraído de BuzonJudicial.tsx (antes función interna `statusChip`). */
export const statusConfig: Record<BuzonNucStatusEnum, { color: string; labelKey: string; defaultLabel: string }> = {
  [BuzonNucStatusEnum.HABILITADO]:             { color: '#22c55e', labelKey: 'buzonJudicial.status.habilitadoPorCaso', defaultLabel: 'Habilitado por caso' },
  [BuzonNucStatusEnum.NO_HABILITADO]:          { color: '#9ca3af', labelKey: 'buzonJudicial.status.noHabilitado', defaultLabel: 'No Habilitado' },
  [BuzonNucStatusEnum.PENDIENTE_FIRMA]:        { color: '#3b82f6', labelKey: 'buzonJudicial.status.pendienteFirma', defaultLabel: 'Pendiente Firma' },
  [BuzonNucStatusEnum.PENDIENTE_DESHABILITAR]: { color: '#f97316', labelKey: 'buzonJudicial.status.pendienteDeshabilitar', defaultLabel: 'Pendiente Deshabilitar' },
  [BuzonNucStatusEnum.DESHABILITADO]:          { color: '#ef4444', labelKey: 'buzonJudicial.status.deshabilitado', defaultLabel: 'Deshabilitado' },
  [BuzonNucStatusEnum.PENDIENTE_REHABILITAR]:  { color: '#8b5cf6', labelKey: 'buzonJudicial.status.pendienteHabilitar', defaultLabel: 'Pendiente Habilitar' },
  [BuzonNucStatusEnum.RECHAZADO]:              { color: '#ef4400', labelKey: 'buzonJudicial.status.rechazado', defaultLabel: 'Rechazado' },
};

interface CornerTooltipBadgeProps {
  chip: React.ReactNode;
  icon: React.ReactNode;
  content: React.ReactNode;
  ariaLabel: string;
}

/** Badge circular en la esquina superior derecha del chip, con tooltip al hover — mismo idioma visual
 *  usado para el badge de "Resultado Solicitud" y ahora también para las instancias autorizadas, de
 *  forma que ninguno de los dos ensancha la celda "Estado" (a diferencia de un IconButton aparte). */
const CornerTooltipBadge = ({ chip, icon, content, ariaLabel }: CornerTooltipBadgeProps) => (
  <Badge
    badgeContent={(
      <Tooltip
        title={content}
        arrow
        placement="top"
        enterDelay={220}
        leaveDelay={120}
        slotProps={{
          tooltip: {
            sx: {
              maxWidth: { xs: 250, sm: 340, md: 460 },
              p: '12px 14px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, rgba(15,23,42,0.96) 0%, rgba(30,41,59,0.96) 100%)',
              border: '1px solid rgba(148,163,184,0.35)',
              boxShadow: '0 14px 30px rgba(2,6,23,0.35)',
            },
          },
          arrow: {
            sx: {
              color: 'rgba(15,23,42,0.96)',
            },
          },
        }}
      >
        <Box
          component="span"
          aria-label={ariaLabel}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#475569',
            lineHeight: 1,
            cursor: 'help',
            transition: 'color 160ms ease, transform 160ms ease',
            '&:hover': {
              color: '#1e293b',
              transform: 'translateY(-1px)',
            },
          }}
        >
          {icon}
        </Box>
      </Tooltip>
    )}
    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
    overlap="rectangular"
    sx={{
      '& .MuiBadge-badge': {
        backgroundColor: 'transparent',
        minWidth: 0,
        height: 'auto',
        p: 0,
        border: 'none',
        top: -4,
        right: 6,
      },
    }}
  >
    {chip}
  </Badge>
);

/**
 * Chip de estado del NUC, extraído de BuzonJudicial.tsx. Diferencia "Habilitado por caso" vs
 * "Habilitado por instancia" (punto 9) y agrega el badge informativo con el detalle de las
 * instancias autorizadas (punto 10), según `row.tipoHabilitacion` (1 = NUC, 2 = Instancia) y
 * `row.categoriasAutorizadas` que entrega GET /api/buzon/nucs. Si `tipoHabilitacion` viene
 * `undefined` (registro legado), se degrada a "Habilitado por caso" sin badge informativo.
 */
const BuzonEstadoChip = ({ row }: BuzonEstadoChipProps) => {
  const { t } = useTranslation();

  const esHabilitadoPorInstancia = row.estado === BuzonNucStatusEnum.HABILITADO && row.tipoHabilitacion === 2;
  const categoriasAutorizadas = row.categoriasAutorizadas ?? [];

  const config = esHabilitadoPorInstancia
    ? { color: '#22c55e', labelKey: 'buzonJudicial.status.habilitadoPorInstancia', defaultLabel: 'Habilitado por instancia' }
    : (statusConfig[row.estado] ?? statusConfig[BuzonNucStatusEnum.NO_HABILITADO]);

  const hasDecisionMotivo = Boolean(row.motivoAccion?.trim());
  const chip = (
    <Chip
      label={t(config.labelKey, { defaultValue: config.defaultLabel })}
      sx={{
        backgroundColor: 'transparent',
        color: config.color,
        fontWeight: 600,
        fontSize: '0.85rem',
        height: 32,
        width: 185,
        border: '1.5px solid transparent',
        borderColor: config.color,
        borderRadius: '20px',
        '& .MuiChip-label': { px: 2 },
      }}
    />
  );

  if (hasDecisionMotivo && [BuzonNucStatusEnum.DESHABILITADO, BuzonNucStatusEnum.HABILITADO].includes(row.estado)) {
    return (
      <CornerTooltipBadge
        chip={chip}
        icon={<RiQuestionnaireFill size={14} />}
        ariaLabel={t('buzonJudicial.labels.reason', 'Motivo')}
        content={(
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <Typography
              component="p"
              sx={{
                m: 0,
                fontSize: '0.72rem',
                fontWeight: 700,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: '#bfdbfe',
              }}
            >
              {`${t('buzonJudicial.labels.motivoDeshabilitacion', 'Resultado Solicitud')} #${row.idSolicitud ?? ''}`}
            </Typography>
            <Typography
              component="p"
              sx={{
                m: 0,
                fontSize: '0.83rem',
                lineHeight: 1.55,
                color: '#f8fafc',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                overflowWrap: 'anywhere',
              }}
            >
              {row.motivoAccion}
            </Typography>
          </Box>
        )}
      />
    );
  }

  if (esHabilitadoPorInstancia && categoriasAutorizadas.length > 0) {
    return (
      <CornerTooltipBadge
        chip={chip}
        icon={<RiInformationLine size={14} />}
        ariaLabel={t('buzonJudicial.labels.infoInstanciaButtonAria', 'Ver instancias habilitadas')}
        content={(
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <Typography
              component="p"
              sx={{
                m: 0,
                fontSize: '0.72rem',
                fontWeight: 700,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: '#bfdbfe',
              }}
            >
              {t('buzonJudicial.labels.categoriasAutorizadasTitle', 'Instancia(s) habilitada(s)')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.35 }}>
              {categoriasAutorizadas.map((categoria) => (
                <Typography
                  key={categoria.idCategoriaTribunal}
                  component="p"
                  sx={{ m: 0, fontSize: '0.83rem', lineHeight: 1.4, color: '#f8fafc' }}
                >
                  {categoria.categoriaTribunal}
                </Typography>
              ))}
            </Box>
          </Box>
        )}
      />
    );
  }

  if (!row.alertaFirma) return chip;

  return (
    <Tooltip
      title={t(
        row.alertaFirma === 'REJECTED'
          ? 'buzonJudicial.alerts.firmaRechazada'
          : 'buzonJudicial.alerts.firmaExpirada'
      )}
      arrow
    >
      <Badge
        badgeContent={<RiInformationLine size={12} />}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        overlap="rectangular"
        sx={{
          '& .MuiBadge-badge': {
            backgroundColor: '#f59e0b',
            color: 'white',
            width: 20,
            height: 20,
            minWidth: 20,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            top: -2,
            left: -2,
            border: '2px solid white',
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
            cursor: 'pointer',
          },
        }}
      >
        {chip}
      </Badge>
    </Tooltip>
  );
};

export default BuzonEstadoChip;
